import React from 'react'
import PropTypes from 'prop-types'
import { useD3Selection } from 'hooks'
import { zoom as d3Zoom, mouse, event as d3Event, zoomIdentity } from 'd3'
import isNil from 'lodash/isNil'
import styles from './MouseEvents.scss'

function isZoomOriginEvent() {
  return d3Event.sourceEvent && !['brush', 'end'].includes(d3Event.sourceEvent.type)
}

function MouseEvents({
  width,
  height,
  onZoom,
  onZoomEnd,
  minZoom,
  onMousemove,
  onMouseout,
  confinePanning = true,
  extent,
}) {
  const [ref, useSelectionEffect] = useD3Selection()

  const zoom = React.useMemo(() => d3Zoom(), [])
  const dblClick = React.useRef(false)
  const cursorPosition = React.useRef(false)

  // init zoom width
  React.useEffect(() => {
    zoom.extent([
      [0, 0],
      [width, height],
    ])
    confinePanning &&
      zoom.translateExtent([
        [0, 0],
        [width, height],
      ])
  }, [width, height, confinePanning, zoom])

  // init events
  useSelectionEffect(
    transition => {
      const selection = transition.selection ? transition.selection() : transition

      selection.on('dblclick.chart', () => {
        dblClick.current = true
      })

      zoom.scaleExtent([1, minZoom])
      zoom(selection)

      zoom.on('zoom', () => {
        if (dblClick.current || isZoomOriginEvent()) {
          onZoom && onZoom(d3Event.transform)
        }
      })

      zoom.on('end', () => {
        if (d3Event && cursorPosition.current && (!d3Event.sourceEvent || isZoomOriginEvent())) {
          dblClick.current = false
          onZoomEnd && onZoomEnd(cursorPosition.current)
        }
      })

      selection.on('mousemove.chart', () => {
        cursorPosition.current = mouse(selection.node())
        onMousemove && onMousemove(cursorPosition.current)
      })

      selection.on('mouseout.chart', () => {
        cursorPosition.current = null
        onMouseout && onMouseout()
      })

      // destroy on unmount
      return () => selection.on('.zoom .chart', null)
    },
    [onZoom, onZoomEnd, onMousemove, onMouseout, minZoom]
  )

  // apply zoom transform when extent changes
  useSelectionEffect(
    transition => {
      const selection = transition.selection ? transition.selection() : transition
      const event = d3Event
      const [min, max] = extent
      // Apply zoom triggered by brush selection
      if (!(dblClick.current || (event && event.type === 'zoom' && event.sourceEvent)) && !isNil(min) && !isNil(max)) {
        zoom.transform(selection, zoomIdentity.scale(width / (max - min)).translate(-min, 0))
      }
    },
    [extent, width]
  )

  return <rect height={height} width={width} className={styles.zoom} ref={ref} />
}

MouseEvents.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  minZoom: PropTypes.number,
  extent: PropTypes.array,
  onZoom: PropTypes.func,
  onZoomEnd: PropTypes.func,
  onMousemove: PropTypes.func,
  onMouseout: PropTypes.func,
  confinePanning: PropTypes.bool,
}

export default React.memo(MouseEvents)
